import { supabase } from '@/supabaseClient';
import { Article, User, Sector, ArticleStatus, Attachment, AppSettings, ChatMessage, ChatSession, MaintenanceSettings } from '@/types';

// ==========================================
// MAPPINGS BETWEEN DB AND APP MODELS
// ==========================================

const mapDbSectorToAppSector = (s: any): Sector => ({
  id: s.id,
  name: s.name,
  parentId: s.parent_id
});

const mapAppSectorToDbSector = (s: Sector) => ({
  id: s.id,
  name: s.name,
  parent_id: s.parentId || null
});

const mapDbUserToAppUser = (u: any): User => ({
  id: u.id,
  fullName: u.full_name,
  username: u.username,
  email: u.email,
  sector: u.sector,
  role: u.role,
  avatar: u.avatar || undefined,
  mustChangePassword: u.must_change_password
});

const mapAppUserToDbUser = (u: User) => ({
  id: u.id,
  full_name: u.fullName,
  username: u.username,
  email: u.email,
  sector: u.sector,
  role: u.role,
  avatar: u.avatar || null,
  must_change_password: u.mustChangePassword || false
});

const mapDbArticleToAppArticle = (a: any): Article => ({
  id: a.id,
  title: a.title,
  content: a.content,
  authorId: a.author_id,
  authorName: a.author_name,
  sectorId: a.sector_id,
  sectorName: a.sector_name,
  status: a.status as ArticleStatus,
  createdAt: a.created_at,
  updatedAt: a.updated_at,
  tags: a.tags || [],
  attachments: a.attachments || []
});

const mapAppArticleToDbArticle = (a: Article) => ({
  id: a.id,
  title: a.title,
  content: a.content,
  author_id: a.authorId,
  author_name: a.authorName,
  sector_id: a.sectorId || null,
  sector_name: a.sectorName,
  status: a.status,
  created_at: a.createdAt,
  updated_at: a.updatedAt,
  tags: a.tags,
  attachments: a.attachments
});

const mapDbSessionToAppSession = (s: any): ChatSession => ({
  id: s.id,
  userId: s.user_id,
  title: s.title,
  messages: s.messages || [],
  createdAt: new Date(s.created_at).getTime(),
  updatedAt: new Date(s.updated_at).getTime()
});

// ==========================================
// FILE STORAGE SERVICE
// ==========================================

export const uploadFileToStorage = async (file: File, path: string): Promise<string> => {
  // Sanitiza a chave (path) para evitar o erro "Invalid key" do Supabase Storage decorrente de acentos, espaços e caracteres especiais
  const parts = path.split('/');
  const fileName = parts.pop() || '';
  
  // Normaliza o Unicode para decompor caracteres acentuados em caracteres simples (ex: ç -> c, ã -> a)
  const normalized = fileName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  
  // Separa o nome do arquivo da extensão
  const lastDotIndex = normalized.lastIndexOf(".");
  let cleanFileName = "";
  if (lastDotIndex === -1) {
    // Caso o arquivo não possua extensão
    cleanFileName = normalized.replace(/[^a-zA-Z0-9_-]/g, "_");
  } else {
    const namePart = normalized.substring(0, lastDotIndex);
    const extPart = normalized.substring(lastDotIndex + 1);
    
    // Substitui caracteres não alfanuméricos por sublinhados
    const cleanName = namePart.replace(/[^a-zA-Z0-9_-]/g, "_");
    // Garante que a extensão tenha apenas caracteres válidos
    const cleanExt = extPart.replace(/[^a-zA-Z0-9]/g, "");
    
    // Remove múltiplos sublinhados consecutivos e sublinhados no início/fim
    cleanFileName = `${cleanName.replace(/_+/g, "_").replace(/^_+|_+$/g, "")}.${cleanExt}`;
  }
  
  // Reconstrói o caminho completo sanitizado
  const sanitizedPath = [...parts, cleanFileName].join('/');

  const { error } = await supabase.storage
    .from('attachments')
    .upload(sanitizedPath, file, { cacheControl: '3600', upsert: true });

  if (error) throw error;
  
  const { data } = supabase.storage
    .from('attachments')
    .getPublicUrl(sanitizedPath);

  return data.publicUrl;
};

// ==========================================
// ARTICLES SERVICE
// ==========================================

export const subscribeToArticles = (callback: (articles: Article[]) => void) => {
  let currentArticles: Article[] = [];

  const fetchArticles = () => {
    supabase
      .from('articles')
      .select('*')
      .order('updated_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error("Error fetching articles:", error);
        if (data) {
          currentArticles = data.map(mapDbArticleToAppArticle);
          callback(currentArticles);
        }
      });
  };

  fetchArticles();

  const channel = supabase.channel('articles-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'articles' }, (payload) => {
      console.log("Realtime articles change received:", payload.eventType, payload);
      if (payload.eventType === 'INSERT') {
        const newArt = mapDbArticleToAppArticle(payload.new);
        currentArticles = [newArt, ...currentArticles];
      } else if (payload.eventType === 'UPDATE') {
        const updatedArt = mapDbArticleToAppArticle(payload.new);
        currentArticles = currentArticles.map(a => a.id === updatedArt.id ? updatedArt : a);
      } else if (payload.eventType === 'DELETE') {
        currentArticles = currentArticles.filter(a => a.id !== payload.old.id);
      }
      
      // Keep sorted by updated_at descending
      currentArticles.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      callback(currentArticles);
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const saveArticle = async (article: Article, files: File[]) => {
  console.log("Saving article via Database Service (Supabase)...");

  const newAttachments: Attachment[] = [];
  
  if (files && files.length > 0) {
    for (const file of files) {
      try {
        if (file.size > 50 * 1024 * 1024) { 
           throw new Error(`O arquivo "${file.name}" excede o limite de 50MB.`);
        }
        
        const storagePath = `${article.id}/${Date.now()}_${file.name}`;
        const downloadUrl = await uploadFileToStorage(file, storagePath);

        newAttachments.push({
          id: Date.now().toString() + Math.random().toString(),
          name: file.name,
          type: file.type,
          url: downloadUrl
        });
      } catch (error: any) {
          console.error(`Error processing file ${file.name}:`, error);
          throw new Error(`Erro ao enviar o anexo "${file.name}": ${error.message || JSON.stringify(error)}`);
      }
    }
  }

  const finalAttachments = [...(article.attachments || []), ...newAttachments];
  const articleData: Article = { ...article, attachments: finalAttachments };
  
  const { error } = await supabase
    .from('articles')
    .upsert(mapAppArticleToDbArticle(articleData));

  if (error) {
    console.error("Supabase Save Error:", error);
    throw new Error("Erro ao gravar dados no Supabase: " + error.message);
  }
};

export const updateArticleStatus = async (id: string, status: ArticleStatus) => {
  const { error } = await supabase
    .from('articles')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
};

export const updateArticleSector = async (articleId: string, newSectorId: string, newSectorName: string) => {
  const { error } = await supabase
    .from('articles')
    .update({ 
      sector_id: newSectorId, 
      sector_name: newSectorName,
      updated_at: new Date().toISOString()
    })
    .eq('id', articleId);

  if (error) throw error;
};

export const deleteArticle = async (id: string) => {
  const { error } = await supabase
    .from('articles')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// ==========================================
// USERS SERVICE
// ==========================================

export const subscribeToUsers = (callback: (users: User[]) => void) => {
  let currentUsers: User[] = [];

  const fetchUsers = () => {
    supabase
      .from('users')
      .select('*')
      .then(({ data, error }) => {
        if (error) console.error("Error fetching users:", error);
        if (data) {
          currentUsers = data.map(mapDbUserToAppUser);
          callback(currentUsers);
        }
      });
  };

  fetchUsers();

  const channel = supabase.channel('users-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, (payload) => {
      console.log("Realtime users change received:", payload.eventType, payload);
      if (payload.eventType === 'INSERT') {
        const newUser = mapDbUserToAppUser(payload.new);
        currentUsers = [...currentUsers, newUser];
      } else if (payload.eventType === 'UPDATE') {
        const updatedUser = mapDbUserToAppUser(payload.new);
        currentUsers = currentUsers.map(u => u.id === updatedUser.id ? updatedUser : u);
      } else if (payload.eventType === 'DELETE') {
        currentUsers = currentUsers.filter(u => u.id !== payload.old.id);
      }
      
      // Sort alphabetically by fullName
      currentUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));
      callback(currentUsers);
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const saveUser = async (user: User, password?: string): Promise<{ newUserCreated: boolean, adminSessionRestored: boolean }> => {
  if (user.id.startsWith('new-')) { // New user creation
    if (!password) throw new Error("Senha é obrigatória para novos usuários.");

    // 1. Capture and cache admin session
    const { data: sessionData } = await supabase.auth.getSession();
    const adminSession = sessionData.session;

    try {
      console.log("Supabase Database: Creating Auth user record:", user.email);
      // 2. Sign up the new user
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: user.email,
        password: password,
        options: {
          data: {
            fullName: user.fullName,
            username: user.email.split('@')[0],
            sector: user.sector,
            role: user.role
          }
        }
      });

      if (signUpError) throw signUpError;
      const newUid = signUpData.user?.id;
      if (!newUid) throw new Error("Falha ao obter ID do novo usuário.");

      // 3. Create user profile in public.users table
      const userDataToSave = {
        ...user,
        id: newUid,
        username: user.email.split('@')[0],
        mustChangePassword: user.mustChangePassword ?? true
      };
      
      const { error: profileError } = await supabase
        .from('users')
        .insert(mapAppUserToDbUser(userDataToSave));

      if (profileError) throw profileError;

      // 4. Restore admin session if it was switched
      let adminSessionRestored = false;
      if (adminSession) {
        const { error: restoreError } = await supabase.auth.setSession({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token
        });
        if (!restoreError) {
          adminSessionRestored = true;
          console.log("Supabase Database: Admin session restored successfully.");
        } else {
          console.error("Supabase Database: Error restoring admin session:", restoreError);
        }
      }

      return { newUserCreated: true, adminSessionRestored };

    } catch (e: any) {
      console.error("Supabase Database: Error creating user:", e);
      throw new Error(`Erro ao criar usuário: ${e.message}`);
    }
  } else { // Existing user profile update
    const { error } = await supabase
      .from('users')
      .update(mapAppUserToDbUser(user))
      .eq('id', user.id);

    if (error) throw error;

    if (password) {
      const { error: passwordError } = await supabase.rpc('admin_update_user_password', {
        target_user_id: user.id,
        new_password: password
      });
      if (passwordError) throw passwordError;
    }
    return { newUserCreated: false, adminSessionRestored: true };
  }
};

export const deleteUser = async (id: string) => {
  const { error } = await supabase
    .from('users')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const markPasswordAsChanged = async (userId: string) => {
  const { error } = await supabase
    .from('users')
    .update({ must_change_password: false })
    .eq('id', userId);

  if (error) throw error;
};

// ==========================================
// SECTORS SERVICE
// ==========================================

export const subscribeToSectors = (callback: (sectors: Sector[]) => void) => {
  let currentSectors: Sector[] = [];

  const fetchSectors = () => {
    supabase
      .from('sectors')
      .select('*')
      .order('name')
      .then(({ data, error }) => {
        if (error) console.error("Error fetching sectors:", error);
        if (data) {
          currentSectors = data.map(mapDbSectorToAppSector);
          callback(currentSectors);
        }
      });
  };

  fetchSectors();

  const channel = supabase.channel('sectors-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'sectors' }, (payload) => {
      console.log("Realtime sectors change received:", payload.eventType, payload);
      if (payload.eventType === 'INSERT') {
        const newSec = mapDbSectorToAppSector(payload.new);
        currentSectors = [...currentSectors, newSec];
      } else if (payload.eventType === 'UPDATE') {
        const updatedSec = mapDbSectorToAppSector(payload.new);
        currentSectors = currentSectors.map(s => s.id === updatedSec.id ? updatedSec : s);
      } else if (payload.eventType === 'DELETE') {
        currentSectors = currentSectors.filter(s => s.id !== payload.old.id);
      }
      
      // Sort alphabetically by name
      currentSectors.sort((a, b) => a.name.localeCompare(b.name));
      callback(currentSectors);
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const saveSector = async (sector: Sector) => {
  const { error } = await supabase
    .from('sectors')
    .upsert(mapAppSectorToDbSector(sector));

  if (error) throw error;
};

export const deleteSector = async (id: string) => {
  // Supabase delete sectors
  const { error: deleteError } = await supabase
    .from('sectors')
    .delete()
    .eq('id', id);

  if (deleteError) throw deleteError;

  // Move orphaned children to parent-less top level
  await supabase
    .from('sectors')
    .update({ parent_id: null })
    .eq('parent_id', id);
};

// ==========================================
// APP SETTINGS SERVICE
// ==========================================

const APP_SETTINGS_KEY = 'appSettings';

export const subscribeToAppSettings = (callback: (settings: AppSettings) => void) => {
  const fetchSettings = () => {
    supabase
      .from('settings')
      .select('value')
      .eq('key', APP_SETTINGS_KEY)
      .single()
      .then(({ data, error }) => {
        if (error && error.code !== 'PGRST116') { // PGRST116 is code for no rows returned
          console.error("Error subscribing to App Settings:", error);
        }
        if (data && data.value) {
          callback(data.value as AppSettings);
        } else {
          callback({});
        }
      });
  };

  fetchSettings();

  const channel = supabase.channel('settings-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'settings', filter: `key=eq.${APP_SETTINGS_KEY}` }, () => {
      fetchSettings();
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const saveAppSettings = async (settings: AppSettings) => {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: APP_SETTINGS_KEY, value: settings });

  if (error) throw error;
};

// ==========================================
// CHAT SESSIONS SERVICE
// ==========================================

export const subscribeToChatSessions = (userId: string, callback: (sessions: ChatSession[]) => void) => {
  let currentSessions: ChatSession[] = [];

  const fetchSessions = () => {
    supabase
      .from('chat_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error("Error fetching sessions:", error);
        if (data) {
          currentSessions = data.map(mapDbSessionToAppSession);
          callback(currentSessions);
        }
      });
  };

  fetchSessions();

  const channel = supabase.channel(`chat-sessions-${userId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_sessions', filter: `user_id=eq.${userId}` }, (payload) => {
      console.log("Realtime chat sessions change received:", payload.eventType, payload);
      if (payload.eventType === 'INSERT') {
        const newSession = mapDbSessionToAppSession(payload.new);
        currentSessions = [newSession, ...currentSessions];
      } else if (payload.eventType === 'UPDATE') {
        const updatedSession = mapDbSessionToAppSession(payload.new);
        currentSessions = currentSessions.map(s => s.id === updatedSession.id ? updatedSession : s);
      } else if (payload.eventType === 'DELETE') {
        currentSessions = currentSessions.filter(s => s.id !== payload.old.id);
      }
      
      // Sort by updated_at desc
      currentSessions.sort((a, b) => b.updatedAt - a.updatedAt);
      callback(currentSessions);
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const subscribeToSessionMessages = (sessionId: string, callback: (session: ChatSession | null) => void) => {
  const fetchMessages = () => {
    supabase
      .from('chat_sessions')
      .select('*')
      .eq('id', sessionId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error fetching session messages:", error);
          callback(null);
        } else if (data) {
          callback(mapDbSessionToAppSession(data));
        }
      });
  };

  fetchMessages();

  const channel = supabase.channel(`session-${sessionId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_sessions', filter: `id=eq.${sessionId}` }, (payload) => {
      console.log("Realtime session messages change received:", payload.eventType, payload);
      if (payload.eventType === 'UPDATE') {
        callback(mapDbSessionToAppSession(payload.new));
      } else if (payload.eventType === 'DELETE') {
        callback(null);
      }
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const createChatSession = async (userId: string, initialMessageText: string): Promise<string> => {
  const newSession = {
    user_id: userId,
    title: initialMessageText.substring(0, 50) + (initialMessageText.length > 50 ? '...' : ''),
    messages: [{
      id: Date.now().toString(),
      role: 'user',
      text: initialMessageText,
      timestamp: Date.now()
    }],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('chat_sessions')
    .insert(newSession)
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
};

export const saveSessionMessages = async (sessionId: string, messages: ChatMessage[]) => {
  const { error } = await supabase
    .from('chat_sessions')
    .update({ 
      messages: messages,
      updated_at: new Date().toISOString()
    })
    .eq('id', sessionId);

  if (error) throw error;
};

export const deleteChatSession = async (sessionId: string) => {
  const { error } = await supabase
    .from('chat_sessions')
    .delete()
    .eq('id', sessionId);

  if (error) throw error;
};

// ==========================================
// CHAT SESSIONS MIGRATION SERVICE (STUBS)
// ==========================================

export const checkOldChatSessionsExist = async (): Promise<boolean> => {
  return false; // Migrated to Supabase, no legacy collections exist
};

export const migrateChatSessions = async (progressCallback: (progress: number, message: string) => void): Promise<void> => {
  progressCallback(100, "Concluído.");
  return Promise.resolve();
};

// ==========================================
// MAINTENANCE SETTINGS SERVICE
// ==========================================

const MAINTENANCE_SETTINGS_KEY = 'maintenanceSettings';

export const subscribeToMaintenanceSettings = (callback: (settings: MaintenanceSettings) => void) => {
  const fetchSettings = () => {
    supabase
      .from('settings')
      .select('value')
      .eq('key', MAINTENANCE_SETTINGS_KEY)
      .single()
      .then(({ data, error }) => {
        if (error && error.code !== 'PGRST116') {
          console.error("Error subscribing to Maintenance Settings:", error);
        }
        if (data && data.value) {
          const val = data.value as any;
          callback({
            isMaintenanceMode: val.isMaintenanceMode ?? false,
            message: val.message || "A Wiki da SÓES está temporariamente em manutenção para melhorias. Volte em breve!"
          });
        } else {
          callback({ isMaintenanceMode: false, message: "A Wiki da SÓES está temporariamente em manutenção para melhorias. Volte em breve!" });
        }
      });
  };

  fetchSettings();

  const channel = supabase.channel('maintenance-realtime')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'settings', filter: `key=eq.${MAINTENANCE_SETTINGS_KEY}` }, () => {
      fetchSettings();
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

export const saveMaintenanceSettings = async (settings: MaintenanceSettings) => {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: MAINTENANCE_SETTINGS_KEY, value: settings });

  if (error) throw error;
};